import Button, {ButtonVariant} from 'bitmovin-web-ui/src/components/core/button';
import baseVariables from 'bitmovin-web-ui/src/styles/baseVariables';
import React from 'react';
import {connect} from 'react-redux';
import styled from 'styled-components';

import AccordionPanel from '../../components/accordionPanel';
import BoundImageModal from '../../components/boundImageModal';
import {Link, pxToEm} from '../../components/globalStyles';
import WrapperAppUl from '../../components/wrapperAppUl';
import HardcodedSalesDemoPath from '../../data/hardcodedSalesDemoPath';
import {AppRoutePathFunction} from '../../logic/routes';
import {RootState} from '../../logic/store';
import {LoadingState, State as SalesDemosState} from '../../logic/store/salesDemosStore';
import DeviceSupportPng from '../../media/playerPageView/device_support.svg';
import DrmSupportPng from '../../media/playerPageView/drm-support.svg';

//#region Styled Components

const StyledWrapperDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
`;

const StyledAccordionPanel = styled(AccordionPanel)`
  width: 100%;

  :not(:last-child) {
    margin-bottom: ${pxToEm(18)}em;
  }
`;

const StyledButton = styled(Button)`
  margin-top: 1em;
  margin-right: 1em;
`;

const StyledDrmContentProtectionButtonsWrapper = styled.div`
  display: flex;
`;

const StyledH3 = styled.h3`
  font-size: ${pxToEm(18)}em;
  color: ${baseVariables.colors.midnight};
  margin-top: ${pxToEm(30)}em;
  margin-bottom: ${pxToEm(20)}em;

  &:first-child {
    margin-top: ${pxToEm(10)}em;
  }
`;

//#endregion

enum PlayerAccordionHeadline {
  OfficiallySupportedDevices = 'Officially supported devices',
  CompatibleDevices = 'Compatible devices',
  Browsers = 'Browsers',
  WorkflowsAndProtocols = 'Workflows & Protocols',
  FunctionsAndQuality = 'Functions & Quality',
  SubtitlesAndClosedCaptions = 'Subtitles & Closed Captions',
  ContentProtection = 'Content Protection',
  MetaData = 'Metadata',
  AdvertisingCapabilities = 'Advertising Capabilities',
  OtherPlaybackFeatures = 'Other Playback Features',
  IntegrationAndPartners = 'Integration & Partners',
  StreamLab = 'Stream Lab'
}

interface MapStateToProps {
  salesDemos?: SalesDemosState;
}

interface Props extends MapStateToProps {
  className?: string;
}

interface State {
  expandedAccordionHeadlines: PlayerAccordionHeadline;
}

class PlayerAccordions extends React.Component<Props, State> {
  public state: Readonly<State> = {
    expandedAccordionHeadlines: PlayerAccordionHeadline.OfficiallySupportedDevices
  };

  public componentDidMount(): void {
    this.setState({
      expandedAccordionHeadlines: undefined
    });
  }

  private handleAccordionPanelExpand = (accordionHeadline: PlayerAccordionHeadline) => {
    const isAlreadyExpanded = this.state.expandedAccordionHeadlines === accordionHeadline;
    this.setState({
      expandedAccordionHeadlines: isAlreadyExpanded ? undefined : accordionHeadline
    });
  };

  public render() {
    return (
      <StyledWrapperDiv className={this.props.className}>
        <StyledAccordionPanel
          headline={PlayerAccordionHeadline.OfficiallySupportedDevices}
          expanded={this.state.expandedAccordionHeadlines === PlayerAccordionHeadline.OfficiallySupportedDevices}
          onExpand={() => this.handleAccordionPanelExpand(PlayerAccordionHeadline.OfficiallySupportedDevices)}>
          <WrapperAppUl>
            <li>PC: Mac, Windows & Chromebook</li>
            <li>iPhone, iPad, Apple TV</li>
            <li>Apple AirPlay compatible devices</li>
            <li>Android Phone, Tablet & Android TV</li>
            <li>Android Smart TVs</li>
            <li>Chromecast</li>
            <li>Amazon Fire Tablet & Fire TV</li>
            <li>Roku</li>
            <li>PlayStation 5</li>
            <li>Microsoft Surface, Xbox Series S &amp; X</li>
            <li>Samsung TV (Tizen 2016+)</li>
            <li>LG TV (webOS 2016+)</li>
          </WrapperAppUl>
          <BoundImageModal
            boundComponent={<StyledButton variant={ButtonVariant.OutlinedLight}>Platform Support</StyledButton>}
            source={DeviceSupportPng}
          />
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={PlayerAccordionHeadline.CompatibleDevices}
          expanded={this.state.expandedAccordionHeadlines === PlayerAccordionHeadline.CompatibleDevices}
          onExpand={() => this.handleAccordionPanelExpand(PlayerAccordionHeadline.CompatibleDevices)}>
          <WrapperAppUl>
            <li>Vizio (2020+)</li>
            <li>Comcast X1 Box</li>
            <li>PlayStation 4</li>
            <li>Sky Q Box</li>
            <li>HbbTV (2.0.1+)</li>
            <li>Panasonic (2018+)</li>
            <li>Hisense (2018+)</li>
            <li>Android AOSP</li>
          </WrapperAppUl>
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={PlayerAccordionHeadline.Browsers}
          expanded={this.state.expandedAccordionHeadlines === PlayerAccordionHeadline.Browsers}
          onExpand={() => this.handleAccordionPanelExpand(PlayerAccordionHeadline.Browsers)}>
          <WrapperAppUl>
            <li>Chrome</li>
            <li>Edge</li>
            <li>Firefox</li>
            <li>Opera</li>
            <li>Safari</li>
            <li>Internet Explorer 11 on Windows 8.1+</li>
          </WrapperAppUl>
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={PlayerAccordionHeadline.WorkflowsAndProtocols}
          expanded={this.state.expandedAccordionHeadlines === PlayerAccordionHeadline.WorkflowsAndProtocols}
          onExpand={() => this.handleAccordionPanelExpand(PlayerAccordionHeadline.WorkflowsAndProtocols)}>
          <StyledH3>Video on Demand</StyledH3>
          <WrapperAppUl>
            <li>HLS</li>
            <li>MPEG-DASH</li>
            <li>HSS</li>
          </WrapperAppUl>
          <StyledH3>Live</StyledH3>
          <WrapperAppUl>
            <li>Low Latency HLS (CMAF)</li>
            <li>Low Latency DASH (CMAF)</li>
          </WrapperAppUl>
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={PlayerAccordionHeadline.FunctionsAndQuality}
          expanded={this.state.expandedAccordionHeadlines === PlayerAccordionHeadline.FunctionsAndQuality}
          onExpand={() => this.handleAccordionPanelExpand(PlayerAccordionHeadline.FunctionsAndQuality)}>
          <WrapperAppUl>
            <li>Adaptive Bitrate (ABR)</li>
            <li>Multi-Language Audio </li>
            <li>Offline Playback</li>
            <li>Live DVR</li>
            <li>Real-Data Timestamps</li>
            <li>Picture in Picture</li>
            <li>Frame Accurate Seeking</li>
            <li>React Native SDK</li>
            <li>4K / 8K / 360 / VR</li>
          </WrapperAppUl>
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={PlayerAccordionHeadline.SubtitlesAndClosedCaptions}
          expanded={this.state.expandedAccordionHeadlines === PlayerAccordionHeadline.SubtitlesAndClosedCaptions}
          onExpand={() => this.handleAccordionPanelExpand(PlayerAccordionHeadline.SubtitlesAndClosedCaptions)}>
          <WrapperAppUl>
            <li>WebVTT</li>
            <li>SRT</li>
            <li>TTML/DFXP</li>
            <li>CEA-608/708</li>
            <li>Multi-Language CC</li>
          </WrapperAppUl>
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={PlayerAccordionHeadline.ContentProtection}
          expanded={this.state.expandedAccordionHeadlines === PlayerAccordionHeadline.ContentProtection}
          onExpand={() => this.handleAccordionPanelExpand(PlayerAccordionHeadline.ContentProtection)}>
          <WrapperAppUl>
            <li>Widevine</li>
            <li>Fairplay</li>
            <li>Playready</li>
            <li>AES-128 / Clearkey</li>
            <li>Offline DRM</li>
            <li>Client-side Watermarking</li>
            <li>Key Rotation</li>
            <li>Token Authentication</li>
          </WrapperAppUl>
          <StyledDrmContentProtectionButtonsWrapper>
            <BoundImageModal
              boundComponent={<StyledButton variant={ButtonVariant.OutlinedLight}>DRM Support</StyledButton>}
              source={DrmSupportPng}
            />
            {this.renderDemoLinkButton(HardcodedSalesDemoPath.Drm)}
          </StyledDrmContentProtectionButtonsWrapper>
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={PlayerAccordionHeadline.MetaData}
          expanded={this.state.expandedAccordionHeadlines === PlayerAccordionHeadline.MetaData}
          onExpand={() => this.handleAccordionPanelExpand(PlayerAccordionHeadline.MetaData)}>
          <WrapperAppUl>
            <li>ID3 Tags</li>
            <li>EMSG Box</li>
            <li>CUE Tags</li>
            <li>EventStream (MPD Events)</li>
            <li>Program Date Time</li>
          </WrapperAppUl>
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={PlayerAccordionHeadline.AdvertisingCapabilities}
          expanded={this.state.expandedAccordionHeadlines === PlayerAccordionHeadline.AdvertisingCapabilities}
          onExpand={() => this.handleAccordionPanelExpand(PlayerAccordionHeadline.AdvertisingCapabilities)}>
          <WrapperAppUl>
            <li>Server-Side Ad Insertion (SSAI)</li>
            <li>Client-Side Ad Insertion (CSAI)</li>
            <li>VAST 3.0 / 4.0 / 4.1</li>
            <li>VMAP + Ad Scheduling & Targeting</li>
            <li>VPAID 2.0</li>
            <li>Open Measurement & OM SDK</li>
            <li>Ad Scheduling + Targeting</li>
            <li>Ad Waterfall</li>
            <li>Ad Pods (Pre-Roll, Mid-Roll, Post-Roll)</li>
            <li>Companion Ads</li>
            <li>Overlay Ads on Web</li>
            <li>Skippable Ads</li>
            <li>Ad Macros (Configurable)</li>
            <li>Prefetch VAST/VMAP Tag</li>
            <li>Ad Verification + Ad Viewability</li>
          </WrapperAppUl>
          {this.renderDemoLinkButton(HardcodedSalesDemoPath.Ads)}
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={PlayerAccordionHeadline.OtherPlaybackFeatures}
          expanded={this.state.expandedAccordionHeadlines === PlayerAccordionHeadline.OtherPlaybackFeatures}
          onExpand={() => this.handleAccordionPanelExpand(PlayerAccordionHeadline.OtherPlaybackFeatures)}>
          <WrapperAppUl>
            <li>Preview Thumbnails</li>
            <li>Trickplay (Fast-Forward and Rewind)</li>
            <li>Variable Playback Speed</li>
            <li>Configurable Video Quality Settings</li>
            <li>Audio Only Player</li>
            <li>Network APIs</li>
            <li>Manifest Read APIs</li>
            <li>Buffer Management APIs</li>
            <li>Preload Ad and Video APIs</li>
            <li>Custom UI Skinning</li>
          </WrapperAppUl>
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={PlayerAccordionHeadline.IntegrationAndPartners}
          expanded={this.state.expandedAccordionHeadlines === PlayerAccordionHeadline.IntegrationAndPartners}
          onExpand={() => this.handleAccordionPanelExpand(PlayerAccordionHeadline.IntegrationAndPartners)}>
          <StyledH3>Direct integrations</StyledH3>
          <WrapperAppUl>
            <li>Bitmovin Encoding</li>
            <li>Bitmovin Analytics</li>
          </WrapperAppUl>

          <StyledH3>DRM Partners</StyledH3>
          <WrapperAppUl>
            <li>Irdeto</li>
            <li>Nagra</li>
            <li>BuyDRM (KeyOS certified)</li>
            <li>Verimatrix</li>
            <li>EZDRM</li>
            <li>ExpressPlay</li>
            <li>Axinom</li>
            <li>+ More</li>
          </WrapperAppUl>

          <StyledH3>Advertising Partners</StyledH3>
          <WrapperAppUl>
            <li>SSAI: Google IMA DAI</li>
            <li>SSAI: Yospace</li>
            <li>SSAI: Elemental Media Tailor</li>
            <li>SSAI: Envivio</li>
            <li>CSAI: Google IMA</li>
            <li>CSAI: Freewheel (VAST)</li>
            <li>CSAI: SpotX (VAST)</li>
          </WrapperAppUl>

          <StyledH3>Peer to Peer Integrations</StyledH3>
          <WrapperAppUl>
            <li>Streamroot/Lumen</li>
            <li>Peer5</li>
            <li>Akamai Media Accelerator</li>
          </WrapperAppUl>

          <StyledH3>CDN</StyledH3>
          <WrapperAppUl>
            <li>Akamai</li>
            <li>Fastly</li>
            <li>AWS</li>
            <li>+ More</li>
          </WrapperAppUl>

          <StyledH3>Analytics Partners</StyledH3>
          <WrapperAppUl>
            <li>Conviva</li>
            <li>Akamai Analytics</li>
            <li>Mux</li>
            <li>Nielsen</li>
            <li>Youbora Analytics (NPAW)</li>
            <li>+ More</li>
          </WrapperAppUl>
          {this.renderDemoLinkButton(HardcodedSalesDemoPath.VrPlayer)}
        </StyledAccordionPanel>
        <StyledAccordionPanel
          headline={PlayerAccordionHeadline.StreamLab}
          expanded={this.state.expandedAccordionHeadlines === PlayerAccordionHeadline.StreamLab}
          onExpand={() => this.handleAccordionPanelExpand(PlayerAccordionHeadline.StreamLab)}>
          <WrapperAppUl>
            <li>Fully automated stream testing feature</li>
            <li>Comes with pre-set test cases for over 20+ devices</li>
            <li>Easy set up, allowing you to submit active streams for testing</li>
            <li>See transparent test results from each device tested</li>
            <li>Extensive video testing knowledge not needed</li>
          </WrapperAppUl>
          {this.renderDemoLinkButton(HardcodedSalesDemoPath.StreamLab)}
        </StyledAccordionPanel>
      </StyledWrapperDiv>
    );
  }

  private renderDemoLinkButton = (demoPath: string) => {
    const {loadingState, response} = this.props.salesDemos;

    if (loadingState !== LoadingState.Success) {
      return null;
    }

    const allLoadedDemos = response.demos || [];
    const foundDemo = allLoadedDemos.find(salesDemo => salesDemo.path === demoPath);
    if (foundDemo === undefined) {
      return null;
    }

    return (
      <Link to={AppRoutePathFunction.DemoDetail({demoId: foundDemo.id})}>
        <StyledButton variant={ButtonVariant.OutlinedLight}>{foundDemo.name}&nbsp;Demo</StyledButton>
      </Link>
    );
  };
}

const mapStateToProps = (state: RootState): MapStateToProps => ({
  salesDemos: state.salesDemos
});

export default connect(mapStateToProps)(PlayerAccordions);
